import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  Modal,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import { Search } from "../../../UI/Search/Search";
import { RequiredAsterisk } from "../../../UI/RequiredAsterisk/RequiredAsterisk";

import { Download } from "@mui/icons-material";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import dragDropSvg from "../../../../assets/Icons/SuperAdmin Icons/SuperAdmin _ Manage Organisation/DragDrop.svg";
import countries from "../../../../assets/json/countries.json";
import { useAlert } from "../../../../hooks/useAlert";
import { useParams } from "react-router-dom";
import {
  useGetAllCompaniesQuery,
  useGetAllUsersQuery,
  useGetCompanyAdminQuery,
  useGetUsersByCompanyQuery,
} from "../../../../redux-toolkit/features/company/companyApiSlice";
import {
  useAddEmployeeMutation,
  useBulkUploadMutation,
  useGetAllRolesQuery,
  useGetEmployeeByIdQuery,
  useUpdateEmployeeMutation,
} from "../../../../redux-toolkit/features/employee/employeeApiSlice";
import { useGetAdminManagedCompaniesQuery } from "../../../../redux-toolkit/features/rbac/rbacApiSlice";
import { debounce } from "../../../../utils";
import "./Employee.css";

import rightArrow from "../../../../assets/Icons/arrow.svg";

import { EmployeeTable } from "./EmployeeTable";
import { AccessRoles } from "./AccessModal/AccessRoles";
import { useSelector } from "react-redux";
import { selectSelectedCompany } from "../../../../redux-toolkit/features/auth/authSlice";
import { useCloseOnCompanySwitch } from "../../../../utils/companyContextNavigation";
import { AssignMultiple } from "./AccessModal/AssignMultiple";
import { AdminRolesCards } from "./AccessModal/AdminRolesCards";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

const sectionTabSx = {
  textTransform: "capitalize",
  fontWeight: "500",
  fontSize: "15px",
};

const boxStyling = {
  position: "absolute",
  top: "50%",
  left: "50%",
  right: "50%",
  maxHeight: "85vh",
  overflowY: "auto",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: "25px 40px",
};

const roleStyling = {
  position: "absolute",
  top: "50%",
  left: "50%",
  right: "50%",
  height: "100%",
  maxHeight: "530px",
  overflowY: "auto",
  maxWidth: "40vw",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: "40px",
};

const FormInput = ({ label, required, ...props }) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
      }}
    >
      <h5 className="ftwt__r">
        {label} {required ? <RequiredAsterisk /> : null}
      </h5>
      <TextField {...props} />
    </div>
  );
};

const FormSelect = ({
  label,
  required,
  options,
  inputProps,
  containerStyle,
  optionAction,
  ...props
}) => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        flexDirection: "column",
        ...containerStyle,
      }}
    >
      <h5 className="ftwt__r">
        {label} {required ? <RequiredAsterisk /> : null}
      </h5>
      <Autocomplete
        id="navbar-search"
        isOptionEqualToValue={(op, val) => {
          return op?.value?.toLowerCase() === val?.value?.toLowerCase();
        }}
        options={options}
        renderOption={(props, option) => (
          <li style={{ fontSize: "14px" }} {...props}>
            {option.label}
            {/* Add action in the option from the props */}
            {optionAction ? optionAction(option) : null}
          </li>
        )}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
              }}
              {...inputProps}
              sx={{ borderRadius: "2px" }}
            />
          </>
        )}
        {...props}
      />
    </div>
  );
};

const validationSchema = yup.object({
  fname: yup
    .string("Enter first name")
    .min(1, "Enter first name")
    .required("First name is required"),
  email: yup
    .string("Enter Email")
    .email("Email is invalid")
    .min(1, "Enter Email")
    .required("Email is required"),
  lname: yup
    .string("Enter last name")
    .min(1, "Enter last name")
    .required("Last name is required"),
  company: yup.string("Enter company").required("Company is required"),
  designation: yup.string("Enter designation"),
  doj: yup
    .date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .max(new Date(), "Date of joining cannot be in the future"),
  dob: yup
    .date("Enter date of birth")
    .max(new Date(), "Date of birth cannot be in the future"),
  // TODO: enable once RBAC is configured
  // assignRole: yup.string("Enter assignRole").required("assignRole is required"),
  // permissions: yup
  //   .string("Enter permissions")
  //   .required("permissions is required"),
});

export const Employee = ({ isAdmin }) => {
  const { company } = useParams();
  const selectedCompanyFromRedux = useSelector(selectSelectedCompany);
  const currentUserId = useSelector((state) => state.auth.user?._id);
  const role = useSelector((state) => state.auth.user?.role);
  const allowedAccess =
    useSelector((state) => state.auth.user?.allowedAccess) || [];
  const canManageEmployeeActions =
    role === "SUPERADMIN" ||
    role === "ADMIN" ||
    (role === "CUSTOM_ROLE" &&
      (allowedAccess.includes("SUPERADMIN_EMPLOYEES") ||
        allowedAccess.includes("ADMIN_EMPLOYEES")));
  const canAssignSuperAdmin =
    role === "SUPERADMIN" ||
    (role === "CUSTOM_ROLE" &&
      allowedAccess.includes("SUPERADMIN_EMPLOYEES"));
  const { displayAlert } = useAlert();
  
  // For admin users, use selectedCompany from Redux; otherwise use company from URL params
  const activeCompanyId = isAdmin && selectedCompanyFromRedux ? selectedCompanyFromRedux : company;

  const [openCreateEmployee, setOpenCreateEmployee] = useState(false);
  const [openBulkUpload, setOpenBulkUpload] = useState(false);
  const [openAssignRole, setOpenAssignRole] = useState(false);
  const [grantPromotionAccessForm, setGrantPromotionAccessForm] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [refetchEmp, setRefetchEmp] = useState(false);
  const { data: roles } = useGetAllRolesQuery();
  const superAdminRoleId = roles?.data?.find(
    (r) => r.label === "SUPERADMIN"
  )?.value;
  const [selectedEmployee, setSelectedEmployee] = useState([]);

  const [employeeToBeEdited, setEmployeeToBeEdited] = useState(null);
  const isEditingSelf =
    employeeToBeEdited != null &&
    String(employeeToBeEdited) === String(currentUserId);

  const [openManageEmployeeSelect, setOpenManageEmployeeSelect] =
    useState(false);

  useCloseOnCompanySwitch(() => {
    if (!isAdmin) return;
    setOpenCreateEmployee(false);
    setOpenBulkUpload(false);
    setOpenAssignRole(false);
    setOpenManageEmployeeSelect(false);
    setEmployeeToBeEdited(null);
  });

  const [bulkUploadInCompany, setBulkUploadInCompany] = useState({
    label: "",
    value: "",
  });
  const [selectedBulkUploadFile, setSelectedBulkUploadFile] = useState(null);

  const { data: companies, isLoading: isLoadingAllCompanies } =
    useGetAllCompaniesQuery(undefined, { skip: isAdmin });
  const { data: adminManagedCompanies, isLoading: isLoadingManagedCompanies } =
    useGetAdminManagedCompaniesQuery(currentUserId, {
      skip: !isAdmin || !currentUserId,
    });
  const isLoadingCompanies = isAdmin
    ? isLoadingManagedCompanies
    : isLoadingAllCompanies;
  const [permissions, setPermissions] = useState([]);
  const [isCustom, setIsCustom] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [employeeTab, setEmployeeTab] = useState(0);

  const employeeTabQueryArgs = {
    page: 1,
    limit: 1,
    query: searchQuery,
    sortBy: "createdAt",
    type: "desc",
  };

  const companyTabQueryArgs = {
    companyId: activeCompanyId,
    page: 1,
    limit: 1,
    query: searchQuery,
  };

  const { data: enabledUsersMeta } = useGetAllUsersQuery(
    { ...employeeTabQueryArgs, isActive: true },
    { skip: isAdmin, refetchOnMountOrArgChange: true }
  );

  const { data: disabledUsersMeta } = useGetAllUsersQuery(
    { ...employeeTabQueryArgs, isActive: false },
    { skip: isAdmin, refetchOnMountOrArgChange: true }
  );

  const { data: enabledCompanyUsersMeta } = useGetUsersByCompanyQuery(
    { ...companyTabQueryArgs, isActive: true },
    {
      skip: !isAdmin || !activeCompanyId,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: disabledCompanyUsersMeta } = useGetUsersByCompanyQuery(
    { ...companyTabQueryArgs, isActive: false },
    {
      skip: !isAdmin || !activeCompanyId,
      refetchOnMountOrArgChange: true,
    }
  );

  const enabledCount = isAdmin
    ? enabledCompanyUsersMeta?.data?.users?.totalResults ?? 0
    : enabledUsersMeta?.data?.users?.totalResults ?? 0;
  const disabledCount = isAdmin
    ? disabledCompanyUsersMeta?.data?.users?.totalResults ?? 0
    : disabledUsersMeta?.data?.users?.totalResults ?? 0;

  const employeeSectionTabs = [
    { key: 0, label: "Enabled", count: enabledCount },
    { key: 1, label: "Disabled", count: disabledCount },
  ];

  const handleEmployeeTabChange = (_event, newValue) => {
    setEmployeeTab(newValue);
    setSelectedEmployee([]);
  };

  const {
    data: employeeData,
    refetch: refetchEmployeeById,
  } = useGetEmployeeByIdQuery(employeeToBeEdited, {
    skip: employeeToBeEdited === null,
    refetchOnMountOrArgChange: true,
  });

  const { data: companyAdmin } = useGetCompanyAdminQuery(selectedCompany, {
    skip: selectedCompany === null,
  });

  const [addEmployee] = useAddEmployeeMutation();
  const [updateEmployee] = useUpdateEmployeeMutation();
  const [bulkUpload] = useBulkUploadMutation();

  // some issue with the formik.setFieldError() so used local state for the error
  const [phoneError, setPhoneError] = useState("");

  const handleAssignModalClose = () => {
    setOpenAssignRole(false);
  };

  const emptyEmployeeFormValues = {
    fname: "",
    email: "",
    lname: "",
    dob: "",
    mobile: "",
    company: "",
    location: "",
    designation: "",
    doj: "",
    tenure: "",
    assignRole: "",
    permissions: "",
  };

  const handleClose = () => {
    formik.handleReset();
    setPermissions([]);
    setOpenBulkUpload(false);
    setPhoneError("");

    setEmployeeToBeEdited(null);
    setGrantPromotionAccessForm(false);

    setBulkUploadInCompany({
      label: "",
      value: "",
    });
    setSelectedBulkUploadFile(null);
    setOpenCreateEmployee(false);
  };

  const calculateTenure = () => {
    const today = new Date();

    const doj = new Date(formik.values.doj);

    const tenure =
      today.getMonth() -
      doj.getMonth() +
      12 * (today.getFullYear() - doj.getFullYear());
    const years = Math.floor(tenure / 12);

    formik.setFieldValue(
      "tenure",
      years > 0
        ? `${years} Year${years > 1 ? "s" : ""} ${tenure - years * 12} months`
        : `${tenure > 0 ? tenure : 1} month`
    );
  };

  const submitEmployeeUpdate = async (body) => {
    try {
      await updateEmployee({
        body,
        id: employeeToBeEdited,
      }).unwrap();
      displayAlert({
        open: true,
        message: `Employee Updated Successfully!`,
        type: "success",
        timeout: "1500",
      });
      setRefetchEmp((prev) => !prev);
      setPermissions([]);
      handleClose();
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || `Error while updating the employee`,
        type: "error",
        timeout: "1500",
      });
    }
  };

  const formik = useFormik({
    initialValues: emptyEmployeeFormValues,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        if (!!employeeToBeEdited) {
          const body = {
            ...values,
            mobile: values?.mobile?.toString() || "",
          };
          if (isEditingSelf) {
            delete body.assignRole;
            delete body.permissions;
            delete body.grantPromotionAccess;
          } else {
            body.permissions = permissions;
            body.isCustom = isCustom;

            const newRoleLabel = values.assignRole?.label;
            if (!isCustom && newRoleLabel === "SUPERADMIN") {
              body.grantPromotionAccess = !!grantPromotionAccessForm;
            }
          }
          await submitEmployeeUpdate(body);
        } else {
          await addEmployee({
            ...values,
            mobile: values?.mobile?.toString() || "",
          })
            .unwrap()
            .then(() => {
              displayAlert({
                open: true,
                message: `New Employee Added Successfully!`,
                type: "success",
                timeout: "1500",
              });
              setRefetchEmp((prev) => !prev);
              setPermissions([]);
              handleClose();
            })
            .catch((err) => {
              displayAlert({
                open: true,
                message:
                  err?.data?.message || `Error while creating the employee`,
                type: "error",
                timeout: "1500",
              });
            });
        }
      } catch (error) {
      }
    },
  });

  const handleBulkUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    await bulkUpload({
      formData,
      companyId: bulkUploadInCompany?.value,
    })
      .unwrap()
      .then((res) => {
        setRefetchEmp(!refetchEmp);
        setOpenBulkUpload(false);

        displayAlert({
          open: true,
          message: `Bulk upload successful!`,
          type: "success",
          timeout: "1500",
        });
      })
      .catch((err) => {
        displayAlert({
          open: true,
          message:
            err?.data?.message || `Something went wrong while bulk uploading`,
          type: "error",
          timeout: "6000",
        });
      });
  };

  const checkPhoneError = () => {
    try {
      if (formik.values.mobile && !isValidPhoneNumber(formik.values.mobile)) {
        setPhoneError("Enter a valid phone");
      } else {
        setPhoneError("");
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (!formik.values.mobile) return;
    checkPhoneError();
  }, [formik.values.mobile]);

  useEffect(() => {
    if (!formik.values.doj) {
      formik.setFieldValue("tenure", "");
      return;
    }
    calculateTenure();
  }, [formik.values.doj]);

  useEffect(() => {
    if (employeeToBeEdited && refetchEmployeeById) {
      refetchEmployeeById();
    }
  }, [employeeToBeEdited]);

  const companyOptions = (() => {
    if (isLoadingCompanies) return [];

    if (isAdmin) {
      const options = [];
      const seen = new Set();
      const pushCompany = (companyItem) => {
        const id = companyItem?._id ? String(companyItem._id) : "";
        const name = companyItem?.name;
        if (!id || !name || seen.has(id)) return;
        seen.add(id);
        options.push({ label: name, value: id });
      };

      pushCompany(adminManagedCompanies?.data?.primaryCompany);
      (adminManagedCompanies?.data?.managedCompanies || []).forEach(pushCompany);
      return options;
    }

    if (!companies?.data?.companies) return [];
    return companies.data.companies.map((dep) => ({
      label: dep?.name,
      value: String(dep?._id),
    }));
  })();

  const resolveCompanyOption = (companyId) => {
    if (!companyId) return { label: "", value: "" };
    const id = String(companyId);
    return (
      companyOptions.find((option) => String(option.value) === id) || {
        label: "",
        value: id,
      }
    );
  };

  const applyCreateCompanyDefaults = () => {
    const companyId = activeCompanyId ? String(activeCompanyId) : "";
    formik.resetForm({
      values: {
        ...emptyEmployeeFormValues,
        company: companyId,
      },
    });
    setSelectedCompany(companyId || null);
    setPermissions([]);
    setIsCustom(false);
  };

  const openAddEmployee = () => {
    setEmployeeToBeEdited(null);
    applyCreateCompanyDefaults();
    setOpenCreateEmployee(true);
    setOpenManageEmployeeSelect(false);
  };

  const openBulkUploadModal = () => {
    setBulkUploadInCompany(resolveCompanyOption(activeCompanyId));
    setOpenBulkUpload(true);
    setOpenManageEmployeeSelect(false);
  };

  useEffect(() => {
    if (openCreateEmployee && !employeeToBeEdited && activeCompanyId) {
      const companyId = String(activeCompanyId);
      formik.setFieldValue("company", companyId);
      setSelectedCompany(companyId);
    }
  }, [openCreateEmployee, employeeToBeEdited, activeCompanyId]);

  useEffect(() => {
    if (!openBulkUpload || !activeCompanyId) return;
    setBulkUploadInCompany(resolveCompanyOption(activeCompanyId));
  }, [
    openBulkUpload,
    activeCompanyId,
    isAdmin,
    adminManagedCompanies,
    companies,
  ]);

  useEffect(() => {
    if (employeeToBeEdited == null) {
      return;
    }

    setIsCustom(false);
    setOpenCreateEmployee(true);

    if (!employeeData) {
      return;
    }

    formik.setValues({
      ...employeeData?.data,
      dob: employeeData?.data?.dob?.split("T")[0] ?? "",
      doj: employeeData?.data?.doj?.split("T")[0] ?? "",
      company: employeeData?.data?.company
        ? String(employeeData.data.company)
        : "",
    });
    setSelectedCompany(
      employeeData?.data?.company
        ? String(employeeData.data.company)
        : null
    );

    if (
      employeeData?.data?.permissionSets?.name === "ADMIN" ||
      employeeData?.data?.permissionSets?.name === "EMPLOYEE" ||
      employeeData?.data?.permissionSets?.name === "SUPERADMIN"
    ) {
      const roleOption = roles?.data.filter(
        (item) => item.label === employeeData?.data?.permissionSets?.name
      )[0];
      formik.setFieldValue("assignRole", roleOption);
      setPermissions([]);
    } else {
      const customRole = roles?.data.filter(
        (item) => item.label === "CUSTOMROLE"
      )[0];
      formik.setFieldValue("assignRole", customRole);
      setIsCustom(true);
      setPermissions(employeeData?.data?.permissionSets?.permissions ?? []);
    }
    setGrantPromotionAccessForm(!!employeeData?.data?.promotionAccess);
  }, [employeeData, employeeToBeEdited]);

  const setDebouncedSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 500),
    []
  );
  const roleHandler = (event) => {
    formik.setFieldValue("assignRole", event.target.value);
    if (event.target.value.label === "CUSTOMROLE") {
      setIsCustom(true);
      setPermissions([]);
      setGrantPromotionAccessForm(false);
    } else if (event.target.value.label === "EMPLOYEE") {
      setIsCustom(false);
      setPermissions([]);
      setGrantPromotionAccessForm(false);
    } else {
      setIsCustom(false);
      setPermissions([event.target.value.value]);
      if (event.target.value.label !== "SUPERADMIN") {
        setGrantPromotionAccessForm(false);
      }
    }
  };

  const selectedCompanyOption = formik.values.company
    ? resolveCompanyOption(formik.values.company)
    : null;
  const lockCompanyField = isAdmin || employeeToBeEdited !== null;

  return (
    <div className="container employee-page">
      <Modal
        open={openAssignRole}
        onClose={handleAssignModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            ...roleStyling,
            width: 1150,
          }}
        >
          <div className="employee__modal_header">
            <h1
              style={{
                fontSize: "16px",
              }}
            >
              Assign Role Access
            </h1>
            <CloseIcon
              onClick={handleAssignModalClose}
              sx={{
                fontSize: "18px",
                cursor: "pointer",
                color: "var(--grey400)",
                "&:hover": {
                  color: "var(--error)",
                },
              }}
            />
          </div>

          <AssignMultiple
            handleAssignModalClose={handleAssignModalClose}
            setOpenAssignRole={setOpenAssignRole}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            canAssignSuperAdmin={canAssignSuperAdmin}
            superAdminRoleId={superAdminRoleId}
          />
        </Box>
      </Modal>
      <Modal
        open={openBulkUpload}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            ...boxStyling,
            width: 820,
          }}
        >
          <div className="employee__modal_header">
            <h1
              style={{
                fontSize: "16px",
              }}
            >
              Bulk upload users
            </h1>
            <CloseIcon
              onClick={handleClose}
              sx={{
                fontSize: "18px",
                cursor: "pointer",
                color: "var(--grey400)",
                "&:hover": {
                  color: "var(--error)",
                },
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              alignItems: "end",
              flexDirection: "column",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <div className="employee__bulk_upload_action_header">
              <FormSelect
                containerStyle={{
                  width: "40%",
                }}
                label={"Company"}
                required
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="company"
                disableClearable
                disabled={isAdmin}
                value={
                  bulkUploadInCompany?.value ? bulkUploadInCompany : null
                }
                onChange={(_, val) => {
                  if (isAdmin) return;
                  setBulkUploadInCompany(
                    val
                      ? { label: val.label, value: String(val.value) }
                      : { label: "", value: "" }
                  );
                }}
                options={companyOptions}
              />
              <a
                href="https://quest-static-bucket.s3.ap-south-1.amazonaws.com/sample+file.csv"
                download
              >
                <Button variant="text" endIcon={<Download color="primary" />}>
                  Download sample file
                </Button>
              </a>
            </div>
            <label
              htmlFor="bulk"
              className="employee__bulk_upload_label"
              onClick={() => {
                if (!bulkUploadInCompany?.value) {
                  displayAlert({
                    open: true,
                    message: `Please select a company before uploading a file`,
                    type: "error",
                    timeout: "2000",
                  });
                  return;
                }
              }}
              onDrop={(e) => {
                if (!bulkUploadInCompany?.value) {
                  displayAlert({
                    open: true,
                    message: `Please select a company before uploading a file`,
                    type: "error",
                    timeout: "2000",
                  });
                  return;
                }
                e.preventDefault();
                setSelectedBulkUploadFile(e.dataTransfer.files[0]);
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <img src={dragDropSvg} />
              {selectedBulkUploadFile ? (
                <>
                  <p className="employee__bulk_upload_label_text">
                    {selectedBulkUploadFile?.name}
                  </p>
                </>
              ) : (
                <>
                  <p className="employee__bulk_upload_label_text">
                    Click to upload (CSV format) &nbsp;
                    <span className="employee__bulk_upload_label_text_sm">
                      or drag and drop here
                    </span>
                  </p>
                  <small className="employee__bulk_upload_label_text_sm">
                    Maximum file size 50MB
                  </small>
                </>
              )}
            </label>

            <input
              id="bulk"
              type={"file"}
              disabled={!bulkUploadInCompany?.value}
              hidden
              accept="text/csv"
              onChange={(e) => {
                setSelectedBulkUploadFile(e.target.files[0]);
                e.target.value = null;
              }}
            />
          </div>

          <div className="employee__modal_footer">
            <div className="icon-container">
              <Button type="submit" variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
            </div>
            <Button
              disabled={!selectedBulkUploadFile}
              variant="contained"
              onClick={(e) => {
                e.preventDefault();
                handleBulkUpload(selectedBulkUploadFile);
              }}
            >
              Save
            </Button>
          </div>
        </Box>
      </Modal>
      <Modal
        open={openCreateEmployee}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              ...boxStyling,
              width: "85vw",
            }}
          >
            <div className="employee__modal_header">
              <h1
                style={{
                  fontSize: "16px",
                }}
              >
                Add employee detail
              </h1>
              <CloseIcon
                onClick={handleClose}
                sx={{
                  fontSize: "18px",
                  cursor: "pointer",
                  color: "var(--grey400)",
                  "&:hover": {
                    color: "var(--error)",
                  },
                }}
              />
            </div>
            <div className="employee__form_container">
              <FormInput
                label={"First name"}
                required
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="fname"
                value={formik.values.fname}
                onChange={formik.handleChange}
                error={formik.touched.fname && Boolean(formik.errors.fname)}
                helperText={formik.touched.fname && formik.errors.fname}
              />

              <FormInput
                label={"Last name"}
                required
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="lname"
                value={formik.values.lname}
                onChange={formik.handleChange}
                error={formik.touched.lname && Boolean(formik.errors.lname)}
                helperText={formik.touched.lname && formik.errors.lname}
              />

              <FormInput
                label={"Email ID"}
                type={"email"}
                required
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <FormSelect
                label={"Company"}
                required
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="company"
                disableClearable
                disabled={lockCompanyField}
                value={selectedCompanyOption}
                onChange={(_, val) => {
                  if (lockCompanyField) return;
                  const companyId = val?.value ? String(val.value) : "";
                  formik.setFieldValue("company", companyId);
                  setSelectedCompany(companyId || null);
                }}
                inputProps={{
                  error:
                    formik.touched.company && Boolean(formik.errors.company),
                  helperText: formik.touched.company && formik.errors.company,
                }}
                options={companyOptions}
              />
              <FormInput
                label={"Designation"}
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="designation"
                value={formik.values.designation}
                onChange={formik.handleChange}
                error={
                  formik.touched.designation &&
                  Boolean(formik.errors.designation)
                }
                helperText={
                  formik.touched.designation && formik.errors.designation
                }
              />
              <FormInput
                label={"Date of Joining"}
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="doj"
                value={formik.values.doj}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (inputValue) {
                    const selectedDate = new Date(inputValue);
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    selectedDate.setHours(0, 0, 0, 0);
                    if (selectedDate > today) {
                      formik.setFieldError(
                        "doj",
                        "Date of joining cannot be in the future"
                      );
                      formik.setFieldTouched("doj", true);
                      // Don't update the value if it's a future date
                      return;
                    }
                  }
                  // Clear error and update value if date is valid
                  formik.setFieldError("doj", "");
                  formik.handleChange(e);
                }}
                error={formik.touched.doj && Boolean(formik.errors.doj)}
                helperText={formik.touched.doj && formik.errors.doj}
                type="date"
                InputProps={{
                  inputProps: {
                    max: new Date().toISOString().split("T")[0],
                  },
                }}
              />
              <FormInput
                label={"Date of Birth"}
                id="outlined-basic"
                variant="outlined"
                fullWidth
                name="dob"
                value={formik.values.dob}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  if (inputValue) {
                    const selectedDate = new Date(inputValue);
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    selectedDate.setHours(0, 0, 0, 0);
                    if (selectedDate > today) {
                      formik.setFieldError(
                        "dob",
                        "Date of birth cannot be in the future"
                      );
                      formik.setFieldTouched("dob", true);
                      // Don't update the value if it's a future date
                      return;
                    }
                  }
                  // Clear error and update value if date is valid
                  formik.setFieldError("dob", "");
                  formik.handleChange(e);
                }}
                error={formik.touched.dob && Boolean(formik.errors.dob)}
                helperText={formik.touched.dob && formik.errors.dob}
                type="date"
                InputProps={{
                  inputProps: {
                    max: new Date().toISOString().split("T")[0],
                  },
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                <h5 className="ftwt__r">Telephone</h5>

                <PhoneInput
                  defaultCountry="SE"
                  placeholder="Enter phone number"
                  name="mobile"
                  value={formik.values.mobile}
                  onChange={(val) => {
                    if (!val) {
                      setPhoneError("");
                    }
                    formik.setFieldValue("mobile", val);
                  }}
                  className="employee__phone_input"
                  style={{
                    border: phoneError ? "1px solid #d32f2f" : "",
                  }}
                />
                <small
                  style={{
                    fontSize: "0.75rem",
                    color: "#d32f2f",
                    margin: "3px 14px 0",
                  }}
                >
                  {phoneError}
                </small>
              </div>

              <FormSelect
                label={"Location"}
                id="outlined-basic"
                variant="outlined"
                fullWidth
                disableClearable
                name="location"
                value={{
                  label: formik.values.location,
                  value: formik.values.location,
                }}
                onChange={(_, val) => {
                  formik.setFieldValue("location", val?.value || "");
                }}
                inputProps={{
                  helperText: formik.touched.location && formik.errors.location,
                }}
                options={countries}
              />

              {employeeToBeEdited !== null ? (
                <>
                  <FormInput
                    label={"Reporting manager"}
                    id="outlined-basic"
                    variant="outlined"
                    fullWidth
                    disabled
                    name="dob"
                    readOnly
                    value={
                      companyAdmin?.data?.fname
                        ? `${companyAdmin?.data?.fname} ${companyAdmin?.data?.lname}`
                        : companyAdmin?.data?.email
                    }
                  />

                  <FormInput
                    label={"Tenure"}
                    disabled
                    id="outlined-basic"
                    variant="outlined"
                    fullWidth
                    name="tenure"
                    value={formik.values.tenure}
                  />
                  {formik.values.assignRole && (
                    <FormControl sx={{ width: "100%" }}>
                      <h5 className="ftwt__r">Assign Role</h5>
                      <Select
                        labelId="demo-simple-select-autowidth-label"
                        id="demo-simple-select-autowidth"
                        name="assignRole"
                        defaultValue={formik.values.assignRole}
                        value={formik.values.assignRole}
                        onChange={roleHandler}
                        disabled={isEditingSelf}
                        inputProps={{
                          error:
                            formik.touched.assignRole &&
                            Boolean(formik.errors.assignRole),
                          helperText:
                            formik.touched.assignRole &&
                            formik.errors.assignRole,
                        }}
                      >
                        {roles?.data.map((item, index) => (
                          <MenuItem
                            sx={{ fontSize: "14px" }}
                            key={index}
                            value={item}
                          >
                            {item.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                      {isEditingSelf && (
                        <p
                          style={{
                            marginTop: "6px",
                            fontSize: "12px",
                            color: "var(--grey400)",
                          }}
                        >
                          You cannot update your own permissions
                        </p>
                      )}
                    </FormControl>
                  )}
                </>
              ) : null}
            </div>
            {employeeToBeEdited && isCustom && !isEditingSelf && (
              <div>
                <h5 className="ftwt__r">
                  Permissions <span style={{ color: "var(--error)" }}>*</span>
                </h5>
                <AccessRoles
                  key={employeeToBeEdited}
                  setPermissions={setPermissions}
                  permissions={permissions}
                  showSuperAdminColumn={canAssignSuperAdmin}
                />
              </div>
            )}
            {employeeToBeEdited &&
              !isCustom &&
              !isEditingSelf &&
              canAssignSuperAdmin &&
              formik.values.assignRole?.label === "SUPERADMIN" && (
                <div className="employee__sa-extra-permissions">
                  <h5 className="ftwt__r">Permission</h5>
                  <p className="employee__sa-extra-permissions-subtitle">
                    Additional Superadmin Permission
                  </p>
                  <div className="employee__sa-extra-permissions-card">
                    <AdminRolesCards
                      label="Promotions"
                      descp="Create; Read; Update; Delete"
                      checked={grantPromotionAccessForm}
                      onChange={setGrantPromotionAccessForm}
                    />
                  </div>
                </div>
              )}
            <div className="employee__modal_footer">
              <Button
                type="submit"
                variant="contained"
                onClick={() => {
                  checkPhoneError();
                }}
              >
                {employeeToBeEdited !== null ? "Update" : "Save"}
              </Button>
            </div>
          </Box>
        </form>
      </Modal>
      <div className="employee__page_container">
        <div className="employee__page_header">
          <div>
            <Search
              placehold={"Search by name, email, company..."}
              setSearchVal={(val) => setDebouncedSearchQuery(val)}
            />
          </div>
          <div className="employee__page_actions">
            {canManageEmployeeActions && (
              <div className="employee__actions">
                <Tooltip
                  describeChild
                  title={
                    selectedEmployee.length == 0
                      ? "Select an employee to assign a role"
                      : ""
                  }
                >
                  <span>
                    <Button
                      disabled={selectedEmployee.length == 0}
                      type="button"
                      variant="contained"
                      onClick={() => {
                        setOpenManageEmployeeSelect(false);
                        setOpenAssignRole(true);
                      }}
                    >
                      Assign Role
                    </Button>
                  </span>
                </Tooltip>
                <div className="employee__manage_employee_dd">
                  <div
                    className={
                      (openManageEmployeeSelect ? "open" : "") +
                      " employee__manage_employee_dd_item"
                    }
                  >
                    <div
                      onClick={() => {
                        setOpenManageEmployeeSelect(!openManageEmployeeSelect);
                      }}
                      className="employee__manage_employee_dd_label"
                    >
                      <p value="">Manage employee</p>
                      <div>
                        <img
                          src={rightArrow}
                          style={{ marginBottom: "2px" }}
                          alt=""
                        />
                      </div>
                    </div>
                    {openManageEmployeeSelect ? (
                      <div className="employee__select_menu">
                        <h1 className="select_divider" />
                        <p onClick={openAddEmployee}>Add employee</p>
                        <p onClick={openBulkUploadModal}>Bulk upload</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <Box className="employee__tabs" sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={employeeTab}
              onChange={handleEmployeeTabChange}
              aria-label="employee status tabs"
            >
              {employeeSectionTabs.map((section) => (
                <Tab
                  key={section.key}
                  label={`${section.label} (${section.count})`}
                  sx={sectionTabSx}
                />
              ))}
            </Tabs>
          </Box>
        </Box>
        <EmployeeTable
          onEditClick={(id) => {
            setIsCustom(false);
            setEmployeeToBeEdited(id);
          }}
          setSelectedEmployee={setSelectedEmployee}
          selectedEmployee={selectedEmployee}
          refetchData={refetchEmp}
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          companyId={activeCompanyId}
          isActiveFilter={employeeTab === 0}
        />
      </div>
    </div>
  );
};
